import { Box, Spinner } from "@chakra-ui/react";
import { isAxiosError } from "axios";
import { useParams, useResolvedPath, useSearchParams } from "react-router";

import InvalidRoute from "@/app/pages/InvalidRoute";
import CompanyDisplay from "@/common/components/CompanyDisplay";
import HeaderLayout from "@/common/components/HeaderLayout";
import RouterTabs, {
  Route,
  useActivePathRouteIndex,
} from "@/common/components/RouterTabs";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useUserData } from "@/user/data/useUserData";

const tabSafeParams = (params: URLSearchParams) => {
  const safeParams = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    // Keep only filtering params that should persist between tabs
    if (key === "search") {
      safeParams.append(key, value);
    }
  }
  return safeParams.toString();
};

export default function User() {
  const [searchParams] = useSearchParams();
  const { userId } = useParams();

  const safeParams = tabSafeParams(searchParams);

  const { data: user, error, isLoading, isSuccess } = useUserData(userId);
  const isNotFound = isAxiosError(error) && error.response?.status === 404;

  const tabsRoutes: Route[] = [
    {
      name: "Flags",
      route: {
        pathname: useResolvedPath("flags").pathname,
        search: safeParams,
      },
    },
    {
      name: "Attributes",
      route: {
        pathname: useResolvedPath("attributes").pathname,
        search: safeParams,
      },
    },
  ];

  const activeRouteIndex = useActivePathRouteIndex(tabsRoutes);

  if (isNotFound) {
    return <InvalidRoute />;
  }

  return (
    <HeaderLayout
      title={
        isLoading ? (
          <Box minH={6}>
            <Spinner />
          </Box>
        ) : isSuccess ? (
          <Box
            alignItems="center"
            display="flex"
            flexWrap="wrap"
            gap={2}
            minW={0}
          >
            <UserDisplay
              as="h1"
              user={user}
              fontSize={"lg"}
              fontWeight="medium"
            />
            {(user.companies ?? []).length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1} minW={0} ml={1}>
                {(user.companies ?? []).map((company) => (
                  <CompanyDisplay
                    key={company.id}
                    link
                    company={company}
                    avatarSize="2xs"
                    fontSize="xs"
                    gap={1}
                    maxW="full"
                    _hover={{ opacity: 0.9, textDecoration: "underline" }}
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        ) : undefined
      }
      borderless
    >
      <RouterTabs
        activeRouteIndex={activeRouteIndex}
        routes={tabsRoutes}
        tabListProps={{ px: 3 }}
        titleTemplate={`User › ${user?.name ?? user?.email ?? user?.id} › %s`}
      />
    </HeaderLayout>
  );
}
